/**
 * VersionId is a unique identifier for a version of a document.
 * This is provided by TDP OS whenever new snapshots are created.
 * The absence of VersionId means the document is the latest version.
 * @link https://developer.atlassian.com/platform/tdp-os/rest/api-group-objects/#api-api-v1-buckets-bucketkey-objects-objectid-versions-get
 * @see ObjectStoreClient
 */
export type VersionId = string & { __versionId: symbol };

/**
 * Represents a version of a document.
 * Null is used to represent the latest version.
 */
export type NullableVersionId = VersionId | null;

/**
 * Extension of the VersionId type used by the frontend only, where the latest
 * version is represented as the string 'latest'.
 */
export type FrontendVersionId = 'latest' | VersionId;

export const versionIdToFrontendVersionId = (
  versionId: VersionId | undefined,
): FrontendVersionId => {
  if (versionId) {
    return versionId as FrontendVersionId;
  } else {
    return 'latest';
  }
};

export const frontendVersionIdToVersionId = (
  frontendVersionId: FrontendVersionId,
): VersionId | undefined => {
  if (frontendVersionId !== 'latest') {
    return frontendVersionId as VersionId;
  }
};

/**
 * Converts a string to a VersionId.
 * See {@link VersionId} for more information.
 * @param e any string
 * @returns a VersionId
 */
export const toNullableVersionId = (e?: string): NullableVersionId => {
  if (!e) {
    return null;
  }
  return e as NullableVersionId;
};

/**
 * Converts a string to a FrontendVersionId.
 * See {@link FrontendVersionId} for more information.
 * @param e any string
 * @returns a VersionId
 */
export const toFrontendVersionId = (e: string) => e as FrontendVersionId;

/**
 * Returns the latest version of the document.
 * @returns 'latest' as a VersionId.
 */
export const latestVersion = (): FrontendVersionId => {
  return 'latest' as FrontendVersionId;
};

/**
 * Historical versions represent old snapshots of the board
 * and are enabled via the version query parameter in the application.
 * @remark 'latest' is a special version which represents the current live version.
 */
export const isHistoricalVersion = (version?: FrontendVersionId): boolean => {
  return isVersion(version) && version !== latestVersion();
};

/**
 * Historical versions represent old snapshots of the board
 * and are enabled via the version query parameter in the application.
 */
export const isVersion = (version?: FrontendVersionId): version is FrontendVersionId => {
  return !!version;
};

/**
 * Gets the VersionId from the current window's URL.
 * If a version is not present, it returns the latest version.
 * @returns the current version as a VersionId
 */
export const getVersionFromWindowURL = () => {
  const url = new URL(globalThis.location.href);
  const version = url.searchParams.get('version');
  return version ? toFrontendVersionId(version) : undefined;
};

/**
 * Gets the VersionId from the current window's URL.
 * If a version is not present, it returns the latest version.
 * @param routeUrl the URL to extract the version from. CFE passes this in as
 * the format of a path and query string: e.g. /path/to/resource?version=123
 * @returns the current version as a VersionId
 */
export const getVersionFromURL = (routeUrl: string) => {
  const url = new URLSearchParams(routeUrl);
  const version = url.get('version');
  return version ? toFrontendVersionId(version) : undefined;
};
